/**
 * ProjectsPage.jsx - Pagina progetti con sistema Demo automatico
 * Se utente non ha progetti → crea automaticamente demo da template
 * 100% Database-driven, zero hardcoding
 */

import React, { useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../App';
import { useDemoProject } from '../../hooks/useDemoProject';
import { 
  Plus, FolderKanban, Search, Filter, 
  LayoutGrid, LayoutList, ArrowUpDown, Sparkles
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { SRNavbar, SRFooter } from '../../components/home';
import Breadcrumb from '../../components/Breadcrumb';
import DemoProjectCard, { DemoWelcomeBanner } from '../../components/projects/DemoProjectCard';
import supabase from '../../lib/supabase';

const ProjectsPage = () => {
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const { 
    projects, 
    loading, 
    error, 
    hasDemoProject, 
    demoProject,
    convertToReal,
    duplicate,
    refetch 
  } = useDemoProject();

  // UI State
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('updated_at');
  const [viewMode, setViewMode] = useState('grid');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [showWelcomeBanner, setShowWelcomeBanner] = useState(true);

  // Create form state
  const [newProject, setNewProject] = useState({
    name: '',
    description: '',
    client: '',
    cover_image: ''
  });

  // Filter and sort projects
  const filteredProjects = React.useMemo(() => {
    let filtered = [...projects];
    
    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(p => 
        p.name?.toLowerCase().includes(query) ||
        p.description?.toLowerCase().includes(query) ||
        p.client?.toLowerCase().includes(query)
      );
    }
    
    // Sort
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return (a.name || '').localeCompare(b.name || '');
        case 'created_at':
          return new Date(b.created_at) - new Date(a.created_at);
        case 'updated_at':
        default:
          return new Date(b.updated_at) - new Date(a.updated_at);
      }
    });
    
    return filtered;
  }, [projects, searchQuery, sortBy]);

  // Create new project
  const handleCreateProject = async (e) => {
    e.preventDefault();
    
    if (!newProject.name.trim()) {
      toast.error('Inserisci un nome per il progetto');
      return;
    }
    
    setIsCreating(true);
    
    try {
      const { data, error: createError } = await supabase
        .from('projects')
        .insert({
          user_id: user.id,
          name: newProject.name.trim(),
          description: newProject.description.trim() || null,
          client: newProject.client.trim() || null,
          cover_image: newProject.cover_image.trim() || null,
          is_demo: false,
          status: 'active'
        })
        .select()
        .single();
      
      if (createError) throw createError;
      
      toast.success('Progetto creato con successo!');
      setShowCreateDialog(false);
      setNewProject({ name: '', description: '', client: '', cover_image: '' });
      
      // Refresh projects list
      await refetch();
      
      // Navigate to new project
      navigate(`/project/${data.id}`);
      
    } catch (err) {
      console.error('Create project error:', err);
      toast.error('Errore nella creazione del progetto');
    } finally {
      setIsCreating(false);
    }
  };

  // Delete project
  const handleDeleteProject = async (projectId) => {
    const project = projects.find(p => p.id === projectId);
    const confirmMessage = project?.is_demo 
      ? 'Eliminare il progetto demo? Verrà ricreato automaticamente al prossimo accesso.'
      : 'Eliminare questo progetto? Questa azione non può essere annullata.';
    
    if (!window.confirm(confirmMessage)) return;
    
    try {
      const { error: deleteError } = await supabase
        .from('projects')
        .delete()
        .eq('id', projectId);
      
      if (deleteError) throw deleteError;
      
      toast.success('Progetto eliminato');
      await refetch();
      
    } catch (err) {
      console.error('Delete project error:', err);
      toast.error('Errore nell\'eliminazione');
    }
  };

  // Handle duplicate
  const handleDuplicate = async (projectId, newName) => {
    const { error: dupError } = await duplicate(projectId, newName);
    if (dupError) {
      toast.error('Errore nella duplicazione');
    }
  };

  // Only show welcome banner if user has only demo project
  const shouldShowWelcome = showWelcomeBanner && hasDemoProject && projects.length === 1;

  return (
    <div className="min-h-screen bg-[#0A0A09]">
      <SRNavbar />
      
      <main className="max-w-[1800px] mx-auto px-6 md:px-12 py-12 pt-28">
        {/* Breadcrumb */}
        <Breadcrumb currentPage="Progetti" />
        
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-8">
          <div>
            <p className="text-sm text-white/40 mb-2 uppercase tracking-wider">Workspace</p>
            <h1 className="text-4xl md:text-5xl font-light text-white" data-testid="projects-title">
              I Tuoi <span className="italic font-serif text-[#b08b5c]">Progetti</span>
            </h1>
          </div>
          
          <Button 
            onClick={() => setShowCreateDialog(true)}
            className="mt-6 md:mt-0 bg-[#b08b5c] hover:bg-[#c9a577] text-white"
            data-testid="create-project-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nuovo Progetto
          </Button>
        </div>

        {/* Welcome Banner (for demo users) */}
        {shouldShowWelcome && (
          <DemoWelcomeBanner onDismiss={() => setShowWelcomeBanner(false)} />
        )}

        {/* Filters Bar */}
        <div className="flex flex-col md:flex-row gap-4 mb-8">
          {/* Search */}
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Cerca progetti..."
              className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/30"
              data-testid="search-projects-input"
            />
          </div>
          
          {/* Sort */}
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-48 bg-white/5 border-white/10 text-white">
              <ArrowUpDown className="w-4 h-4 mr-2 text-white/40" />
              <SelectValue placeholder="Ordina per" />
            </SelectTrigger>
            <SelectContent className="bg-[#1a1a1f] border-white/10">
              <SelectItem value="updated_at" className="text-white hover:bg-white/10">
                Ultimo aggiornamento
              </SelectItem>
              <SelectItem value="created_at" className="text-white hover:bg-white/10">
                Data creazione
              </SelectItem>
              <SelectItem value="name" className="text-white hover:bg-white/10">
                Nome A-Z
              </SelectItem>
            </SelectContent>
          </Select>
          
          {/* View Toggle */}
          <div className="flex items-center gap-1 bg-white/5 rounded-lg p-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setViewMode('grid')}
              className={`h-8 px-3 ${viewMode === 'grid' ? 'bg-white/10 text-white' : 'text-white/40 hover:text-white'}`}
            >
              <LayoutGrid className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setViewMode('list')}
              className={`h-8 px-3 ${viewMode === 'list' ? 'bg-white/10 text-white' : 'text-white/40 hover:text-white'}`}
            >
              <LayoutList className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="aspect-[4/3] bg-white/5 rounded-xl mb-4"></div>
                <div className="h-5 bg-white/5 w-3/4 rounded mb-2"></div>
                <div className="h-4 bg-white/5 w-1/2 rounded"></div>
              </div>
            ))}
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="text-center py-16">
            <div className="w-16 h-16 rounded-2xl bg-red-500/10 border border-red-500/20 flex items-center justify-center mx-auto mb-4">
              <FolderKanban className="w-8 h-8 text-red-400" />
            </div>
            <p className="text-red-400 mb-2">Errore nel caricamento dei progetti</p>
            <p className="text-white/40 text-sm mb-4">{error.message}</p>
            <Button 
              onClick={refetch}
              variant="outline"
              className="border-white/10 text-white hover:bg-white/10"
            >
              Riprova
            </Button>
          </div>
        )}

        {/* Empty State (should rarely show due to auto-demo) */}
        {!loading && !error && filteredProjects.length === 0 && (
          <div className="text-center py-16">
            <div className="w-16 h-16 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center mx-auto mb-4">
              <FolderKanban className="w-8 h-8 text-white/20" />
            </div>
            <h3 className="text-xl font-light text-white mb-2">
              {searchQuery ? 'Nessun risultato' : 'Nessun progetto'}
            </h3>
            <p className="text-white/40 text-sm mb-6 max-w-md mx-auto">
              {searchQuery 
                ? `Nessun progetto corrisponde a "${searchQuery}"`
                : 'Crea il tuo primo progetto per iniziare a organizzare i materiali per i tuoi clienti.'
              }
            </p>
            {!searchQuery && (
              <Button 
                onClick={() => setShowCreateDialog(true)}
                className="bg-[#b08b5c] hover:bg-[#c9a577] text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                Crea Progetto
              </Button>
            )}
          </div>
        )}

        {/* Projects Grid */}
        {!loading && !error && filteredProjects.length > 0 && (
          <div className={
            viewMode === 'grid'
              ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6'
              : 'space-y-4'
          }>
            {filteredProjects.map((project) => (
              <DemoProjectCard
                key={project.id}
                project={project}
                onDuplicate={handleDuplicate}
                onDelete={handleDeleteProject}
                onCreateNew={() => setShowCreateDialog(true)}
                testId={`project-card-${project.id}`}
              />
            ))}
          </div>
        )}

        {/* Project Count */}
        {!loading && filteredProjects.length > 0 && (
          <div className="mt-8 text-center">
            <p className="text-sm text-white/30">
              {filteredProjects.length} progett{filteredProjects.length === 1 ? 'o' : 'i'} trovato/i
              {hasDemoProject && (
                <span className="inline-flex items-center gap-1 ml-2 text-[#b08b5c]">
                  <Sparkles className="w-3 h-3" />
                  Include demo
                </span>
              )}
            </p>
          </div>
        )}
      </main>

      {/* Create Project Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="bg-[#0a0a0f] border-white/10 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl font-light">Nuovo Progetto</DialogTitle>
            <DialogDescription className="text-white/50">
              Crea un nuovo progetto per organizzare i materiali per i tuoi clienti.
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleCreateProject} className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="project-name" className="text-white/70">
                Nome del progetto *
              </Label>
              <Input
                id="project-name"
                value={newProject.name}
                onChange={(e) => setNewProject(prev => ({ ...prev, name: e.target.value }))}
                placeholder="es. Villa Rossi - Cucina"
                className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
                required
                autoComplete="off"
                data-testid="new-project-name"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="project-client" className="text-white/70">
                Cliente / Riferimento
              </Label>
              <Input
                id="project-client"
                value={newProject.client}
                onChange={(e) => setNewProject(prev => ({ ...prev, client: e.target.value }))}
                placeholder="es. Famiglia Rossi"
                className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
                autoComplete="off"
                data-testid="new-project-client"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="project-description" className="text-white/70">
                Descrizione
              </Label>
              <Textarea
                id="project-description"
                value={newProject.description}
                onChange={(e) => setNewProject(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Descrivi brevemente il progetto..."
                className="bg-white/5 border-white/10 text-white placeholder:text-white/30 min-h-[80px]"
                data-testid="new-project-description"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="project-cover" className="text-white/70">
                Immagine di copertina (URL)
              </Label>
              <Input
                id="project-cover"
                value={newProject.cover_image}
                onChange={(e) => setNewProject(prev => ({ ...prev, cover_image: e.target.value }))}
                placeholder="https://..."
                className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
                autoComplete="off"
                data-testid="new-project-cover"
              />
              <p className="text-xs text-white/30">
                Lascia vuoto per usare l'immagine del primo materiale
              </p>
            </div>
            
            <div className="flex justify-end gap-3 pt-4">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setShowCreateDialog(false)}
                className="text-white/70 hover:text-white hover:bg-white/10"
              >
                Annulla
              </Button>
              <Button
                type="submit"
                disabled={isCreating || !newProject.name.trim()}
                className="bg-[#b08b5c] hover:bg-[#c9a577] text-white"
                data-testid="submit-new-project"
              >
                {isCreating ? (
                  <>
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                    Creazione...
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4 mr-2" />
                    Crea Progetto
                  </>
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <SRFooter />
    </div>
  );
};

export default ProjectsPage;
